<script lang="ts">
import { PropType, VNode, defineComponent, h, resolveComponent } from 'vue';

import { IOpenBalanceListItem } from '@/bundles/Deal/types';
import { formatCurrency, formatDate } from '@/utils/filters';
import { getAccountRouteHandler } from '@/bundles/App/helpers';
import i18n from '@/bundles/Localization';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';

export default defineComponent({
  name: 'OpenBalancesMobileCard',

  inheritAttrs: false,

  props: {
    item: {
      type: Object as PropType<IOpenBalanceListItem>,
      required: true
    },
    activeAccountId: {
      type: Number,
      required: true
    }
  },

  render (): VNode | VNode[] {
    const props = this.$props as { item: IOpenBalanceListItem; activeAccountId: number };

    const VCard = resolveComponent('v-card');
    const RouterLink = resolveComponent('router-link');
    const VSpacer = resolveComponent('v-spacer');
    const VDivider = resolveComponent('v-divider');

    return h(VCard, {
      variant: 'outlined',
      ...this.$attrs,
      class: ['open-balances-mobile-card', this.$attrs.class],
    }, { default: () => [
      h('div', { class: 'd-flex align-center pa-3' }, [
        h(RouterLink, {
          class: 'open-balances-mobile-card__title app-link',
          to: getAccountRouteHandler({
            name: 'deal',
            params: { id: props.item._key },
            activeAccountId: props.activeAccountId,
          })
        }, { default: () => props.item.name }),
        h(VSpacer),
        h('span', { class: 'open-balances-mobile-card__transaction-date text-right' }, [
          'Transaction Date: ',
          h('span', formatDate(props.item.transactionDate))
        ])
      ]),
      h(VDivider),
      h('div', { class: 'pa-3 d-flex flex-column gap-3' }, [
        h('div', { class: 'd-flex gap-3' }, [
          h('div', { class: 'open-balances-mobile-card__info' }, [
            h('div', { class: 'open-balances-mobile-card__info-label' }, 'Total Commission:'),
            h('div', { class: 'open-balances-mobile-card__expected' }, formatCurrency(props.item.expected))
          ]),
          h('div', { class: 'open-balances-mobile-card__info' }, [
            h('div', { class: 'open-balances-mobile-card__info-label' }, 'Total In-House Commission:'),
            h('div', { class: 'open-balances-mobile-card__in-house-expected' }, formatCurrency(props.item.inHouseExpected))
          ])
        ]),
        h('div', { class: 'd-flex gap-3' }, [
          h('div', { class: 'open-balances-mobile-card__info' }, [
            h('div', { class: 'open-balances-mobile-card__info-label' }, 'Total Paid:'),
            h('div', { class: 'open-balances-mobile-card__payments' }, formatCurrency(props.item.payments))
          ]),
          h('div', { class: 'open-balances-mobile-card__info' }, [
            h('div', { class: 'open-balances-mobile-card__info-label text-capitalize' }, `${i18n.tc(LocalizationEnum.broker)} Gross:`),
            h('div', { class: 'open-balances-mobile-card__broker-gross' }, formatCurrency(props.item.brokerGross))
          ])
        ]),
        h('div', { class: 'd-flex gap-3' }, [
          h('div', { class: 'open-balances-mobile-card__info' }, [
            h('div', { class: 'open-balances-mobile-card__info-label' }, 'Total Outstanding:'),
            h('div', { class: 'open-balances-mobile-card__remaining' }, formatCurrency(props.item.remaining))
          ]),
          h('div', { class: 'open-balances-mobile-card__info' }, [
            h('div', { class: 'open-balances-mobile-card__info-label text-capitalize' }, `${i18n.tc(LocalizationEnum.broker)} Gross Outstanding:`),
            h('div', { class: 'open-balances-mobile-card__broker-gross-remaining' }, formatCurrency(props.item.brokerGrossRemaining))
          ])
        ])
      ])
    ] });
  }
});
</script>

<style lang="scss" scoped>
// The card is this component's root, so the rule sits on it directly (Vue 2's
// `::v-deep.x` compiled onto the root; `:deep()` only reaches descendants).
.open-balances-mobile-card {
  position: relative;
  font-size: 13px;

  :deep(.open-balances-mobile-card__title) {
    font-weight: 600;
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  }

  :deep(.open-balances-mobile-card__info) {
    flex: 1 1 50%;
  }

  :deep(.open-balances-mobile-card__info-label) {
    font-weight: 600;
  }
}
</style>
